"use client";

import React from 'react';
import { apiFetch } from '@/lib/api';
import VideoGalleryCard, { VideoGalleryCardProps } from '@/components/VideoGalleryCard';
import { useInView } from '@/hooks/useInView';

type Props = {
  initial: VideoGalleryCardProps[];
  sort: string;
};

export default function VideoInfinite({ initial, sort }: Props) {
  const [items, setItems] = React.useState<VideoGalleryCardProps[]>(initial || []);
  const [loading, setLoading] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const { ref, inView } = useInView<HTMLDivElement>({ rootMargin: '600px' });

  const fetchNext = React.useCallback(async () => {
    if (loading || done) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('take', '12');
      params.set('skip', String(items.length));
      if (sort && sort !== 'latest') params.set('sort', sort);
      // apiFetch, not a bare fetch: the API is bearer-only, so an unauthenticated
      // request is answered as a GUEST. Page 1 is server-rendered WITH the
      // viewer's token, so a bare fetch here made every page after the first
      // come back guest-shaped: blurred thumbnails, blurred titles, and
      // isLikedByMe forced to false for a viewer who had in fact liked the
      // video. Same apiFetch-not-bare-fetch trap as the upload poller.
      const res = await apiFetch(`/api/video/v2?${params.toString()}`, { method: 'GET', cache: 'no-store' });
      if (!res.ok) { setDone(true); return; }
      const json: any = await res.json();
      const list = Array.isArray(json?.items) ? json.items : (Array.isArray(json) ? json : []);
      if (list.length === 0) { setDone(true); return; }
      const next: VideoGalleryCardProps[] = list.map((v: any) => ({
        id: Number(v.id),
        title: String(v.title ?? ''),
        thumbnailUrl: String(v.thumbnailUrl ?? v.thumbnailKey ?? ''),
        // Hidden on the browse page (Jason 2026-07-30) — must match the SSR
        // mapper in app/videos/page.tsx or counts pop in after page 1.
        viewCount: null,
        unsafe: Boolean(v.isUnsafeContent ?? v.IsUnsafeContent ?? false),
        // Presentation only; must match the SSR mapper in app/videos/page.tsx.
        audience: typeof (v.audience ?? v.Audience) === 'string' ? String(v.audience ?? v.Audience) : undefined,
        // Drives the like control; must match the SSR mapper too.
        isLikedByMe: typeof (v.isLikedByMe ?? v.IsLikedByMe) === 'boolean' ? Boolean(v.isLikedByMe ?? v.IsLikedByMe) : undefined,
        // Drives the muted in-view preview; must match the SSR mapper too.
        contentUrl: typeof (v.contentUrl ?? v.ContentUrl) === 'string' ? String(v.contentUrl ?? v.ContentUrl) : null,
      }));
      setItems(prev => {
        const seen = new Set(prev.map(v => v.id));
        const dedup = next.filter(v => !seen.has(v.id));
        return prev.concat(dedup);
      });
    } catch {
      setDone(true);
    } finally {
      setLoading(false);
    }
  }, [loading, done, items.length, sort]);

  React.useEffect(() => {
    if (inView) fetchNext();
  }, [inView, fetchNext]);

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map((v) => (
          <VideoGalleryCard key={v.id} {...v} />
        ))}
      </div>
      {!done && (
        <div ref={ref} className="h-10" aria-hidden />
      )}
    </>
  );
}
